import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, UserPlus, Copy, Ban, Sun, Moon, Monitor, Pencil, Trash2, RotateCcw,
  CheckCircle2, Circle, ArrowRight, Building2, Users, Package, Truck, Warehouse,
  Landmark, Mail, ListChecks, Plus,
} from "lucide-react";
import { useTheme } from "next-themes";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RegistersTab } from "@/pages/settings/RegistersTab";
import { WorkshopTab } from "@/pages/settings/WorkshopTab";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLES = ["company_admin", "sales", "purchasing", "warehouse", "field_service", "finance", "auditor"];

function CompanyTab() {
  const { t } = useI18n();
  const { can } = useAuth();
  const qc = useQueryClient();
  const readOnly = !can("settings:write");
  const [form, setForm] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["company"], queryFn: async () => (await api.get("/settings/company")).data });
  useEffect(() => {
    if (data) {
      setForm(data);
      setBankAccounts(data.bank_accounts || []);
    }
  }, [data]);
  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { bank_accounts, ...payload } = form;
      await api.put("/settings/company", payload);
      await api.put("/settings/bank-accounts", bankAccounts.map((account, index) => ({
        bank_name: account.bank_name || "",
        iban: (account.iban || "").replace(/\s/g, ""),
        bic: (account.bic || "").replace(/\s/g, ""),
        is_primary: index === 0,
      })));
      toast.success(t("settings.saved"));
      qc.invalidateQueries({ queryKey: ["company"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.company_details")}</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5"><Label>{t("common.name")}</Label><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} disabled={readOnly} data-testid="company-name-input" /></div>
          <div className="space-y-1.5"><Label>{t("settings.legal_name")}</Label><Input value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("customers.vat_id")}</Label><Input value={form.vat_id || ""} onChange={(e) => set("vat_id", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("settings.tax_number")}</Label><Input value={form.tax_number || ""} onChange={(e) => set("tax_number", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("common.email")}</Label><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("common.phone")}</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5 md:col-span-2"><Label>{t("customers.line1")}</Label><Input value={form.address_line1 || ""} onChange={(e) => set("address_line1", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("customers.postal_code")}</Label><Input value={form.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("customers.city")}</Label><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} disabled={readOnly} /></div>
        </div>
      </Card>
      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.bank_accounts")}</h3>
            <p className="mt-1 text-xs text-slate-500">{t("settings.bank_accounts_desc")}</p>
          </div>
          {!readOnly && <Button variant="outline" size="sm" onClick={() => setBankAccounts((items) => [...items, { bank_name: "", iban: "", bic: "" }])}><Plus className="h-4 w-4" />{t("settings.add_bank")}</Button>}
        </div>
        {bankAccounts.length === 0 ? (
          <div className="rounded-lg border border-dashed p-5 text-center text-sm text-slate-500">{t("settings.no_bank_accounts")}</div>
        ) : (
          <div className="space-y-4">
            {bankAccounts.map((account, index) => (
              <div key={account.id || index} className="grid grid-cols-1 gap-3 rounded-lg border p-4 md:grid-cols-[1fr_1.5fr_1fr_auto]">
                <div className="space-y-1.5"><Label>{t("settings.bank_name")}</Label><Input value={account.bank_name || ""} onChange={(e) => setBankAccounts((items) => items.map((item, i) => i === index ? { ...item, bank_name: e.target.value } : item))} disabled={readOnly} /></div>
                <div className="space-y-1.5"><Label>{t("settings.iban")}</Label><Input value={account.iban || ""} onChange={(e) => setBankAccounts((items) => items.map((item, i) => i === index ? { ...item, iban: e.target.value.toUpperCase() } : item))} disabled={readOnly} /></div>
                <div className="space-y-1.5"><Label>{t("settings.bic")}</Label><Input value={account.bic || ""} onChange={(e) => setBankAccounts((items) => items.map((item, i) => i === index ? { ...item, bic: e.target.value.toUpperCase() } : item))} disabled={readOnly} /></div>
                {!readOnly && <Button className="self-end" variant="ghost" size="icon" onClick={() => setBankAccounts((items) => items.filter((_, i) => i !== index))}><Trash2 className="h-4 w-4 text-red-500" /><span className="sr-only">{t("common.delete")}</span></Button>}
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-6">
        <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.numbering")}</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-1.5"><Label>{t("settings.quote_prefix")}</Label><Input value={form.quote_number_prefix || ""} onChange={(e) => set("quote_number_prefix", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("settings.order_prefix")}</Label><Input value={form.order_number_prefix || ""} onChange={(e) => set("order_number_prefix", e.target.value)} disabled={readOnly} /></div>
          <div className="space-y-1.5"><Label>{t("settings.customer_prefix")}</Label><Input value={form.customer_number_prefix || ""} onChange={(e) => set("customer_number_prefix", e.target.value)} disabled={readOnly} /></div>
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.documents")}</h3>
        <div className="flex items-center gap-3">
          <Switch checked={!!form.quote_pdf_show_images} onCheckedChange={(v) => set("quote_pdf_show_images", v)} disabled={readOnly} id="pdf-img" data-testid="pdf-images-switch" />
          <div><Label htmlFor="pdf-img">{t("settings.pdf_images")}</Label><p className="text-xs text-slate-500">{t("settings.pdf_images_desc")}</p></div>
        </div>
      </Card>
      {!readOnly && <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="company-save-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("common.save")}</Button></div>}
    </div>
  );
}

const SETUP_ICONS = {
  company: Building2, bank: Landmark, customers: Users, suppliers: Truck,
  products: Package, team: Users, email: Mail, stock: Warehouse, datev: Landmark,
};

function SetupTab({ onOpenTab }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["onboarding"],
    queryFn: async () => (await api.get("/settings/onboarding")).data,
  });
  if (isLoading || !data) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const open = (target) => {
    if (target.startsWith("tab:")) onOpenTab(target.slice(4));
    else if (target === "server:email") toast.info(t("settings.setup_email_server_hint"));
    else navigate(target);
  };
  return (
    <div className="space-y-5" data-testid="setup-assistant">
      <Card className="overflow-hidden">
        <div className="grid gap-5 p-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="flex items-center gap-2 text-primary"><ListChecks className="h-5 w-5" /><span className="text-sm font-semibold">{t("settings.setup_assistant")}</span></div>
            <h2 className="mt-2 font-heading text-xl font-semibold">{t("settings.setup_title")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("settings.setup_desc")}</p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${data.progress_percent}%` }} /></div>
          </div>
          <div className="text-center md:min-w-32">
            <div className="text-3xl font-bold text-emerald-600">{data.progress_percent}%</div>
            <div className="text-xs text-slate-500">{data.required_completed} / {data.required_total} {t("settings.setup_required")}</div>
          </div>
        </div>
      </Card>
      <div className="grid gap-3 lg:grid-cols-2">
        {data.steps.map((step) => {
          const Icon = SETUP_ICONS[step.key] || Circle;
          return (
            <Card key={step.key} className="flex items-center gap-4 p-4" data-testid={`setup-step-${step.key}`}>
              <div className={`rounded-full p-2.5 ${step.complete ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}><Icon className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-medium">{t(`settings.setup_${step.key}`)}</h3>
                  {!step.required && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500 dark:bg-slate-800">{t("settings.setup_optional")}</span>}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{t(`settings.setup_${step.key}_desc`)}</p>
              </div>
              {step.complete ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" /> : (
                <Button variant="ghost" size="sm" onClick={() => open(step.target)}>{t("settings.setup_open")}<ArrowRight className="h-4 w-4" /></Button>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function TeamTab() {
  const { t, fmtDate } = useI18n();
  const { can, user } = useAuth();
  const qc = useQueryClient();
  const manage = can("users:manage");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales");
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/team/members")).data });
  const { data: invites } = useQuery({
    queryKey: ["invitations"],
    queryFn: async () => (await api.get("/team/invitations")).data,
    refetchInterval: 30000,
  });

  const invite = async () => {
    if (!email) return;
    setInviting(true);
    try {
      const { data } = await api.post("/team/invitations", { email, tenant_role: role });
      const link = `${window.location.origin}/invite/${data.token}`;
      navigator.clipboard.writeText(link).catch(() => {});
      if (data.email_status === "failed") {
        toast.warning(`${t("settings.invite_send_failed")} ${t("quotes.link_copied")}`);
      } else {
        toast.success(`${t("settings.invite_send_success")} ${t("quotes.link_copied")}`);
      }
      setEmail("");
      qc.invalidateQueries({ queryKey: ["invitations"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setInviting(false); }
  };
  const saveMember = async () => {
    if (!editing) return;
    const passwordRequested = !!editing.new_password;
    if (passwordRequested && editing.new_password !== editing.confirm_password) {
      toast.error(t("errors.passwords_do_not_match"));
      return;
    }
    setBusy(true);
    try {
      if (editing.is_self && passwordRequested) {
        await api.post("/auth/change-password", {
          current_password: editing.current_password,
          new_password: editing.new_password,
        });
      }
      await api.patch(`/team/members/${editing.id}`, {
        full_name: editing.full_name.trim(), email: editing.email.trim(), tenant_role: editing.tenant_role,
      });
      if (!editing.is_self && passwordRequested) {
        await api.post(`/team/members/${editing.id}/reset-password`, { password: editing.new_password });
      }
      toast.success(passwordRequested ? t("settings.member_and_password_saved") : t("settings.member_saved"));
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["members"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };
  const removeMember = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await api.delete(`/team/members/${removing.id}`);
      toast.success(t("settings.member_removed"));
      setRemoving(null);
      qc.invalidateQueries({ queryKey: ["members"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };
  const reactivateMember = async (member) => {
    try {
      await api.patch(`/team/members/${member.id}`, { is_active: true });
      toast.success(t("settings.member_reactivated"));
      qc.invalidateQueries({ queryKey: ["members"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const revoke = async (inv) => {
    try { await api.post(`/team/invitations/${inv.id}/revoke`); qc.invalidateQueries({ queryKey: ["invitations"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const resendInvite = async (inv) => {
    setResendingId(inv.id);
    try {
      const { data } = await api.post(`/team/invitations/${inv.id}/resend`);
      const link = `${window.location.origin}/invite/${data.token}`;
      navigator.clipboard.writeText(link).catch(() => {});
      if (data.email_status === "failed") {
        toast.warning(`${t("settings.invite_send_failed")} ${t("quotes.link_copied")}`);
      } else {
        toast.success(`${t("settings.invite_resend_success")} ${t("quotes.link_copied")}`);
      }
      qc.invalidateQueries({ queryKey: ["invitations"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setResendingId(null); }
  };
  const copyInvite = (inv) => { navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.token}`); toast.success(t("quotes.link_copied")); };
  const emailBadge = (status) => {
    const style = status === "delivered" ? "accepted"
      : status === "failed" ? "rejected"
        : status === "not_sent" ? "inactive"
          : status === "delayed" ? "expired" : "sent";
    return <StatusBadge status={style} labelKey={`settings.invite_email_${status || "not_sent"}`} />;
  };

  return (
    <div className="space-y-6">
      {manage && (
        <Card className="p-6">
          <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.invite_user")}</h3>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input type="email" placeholder={t("common.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="sm:w-56" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={invite} disabled={inviting} data-testid="invite-submit-button"><UserPlus className="h-4 w-4" />{t("settings.invite_user")}</Button>
          </div>
        </Card>
      )}
      <Card>
        <div className="p-4"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.members")}</h3></div>
        <Table data-testid="members-table">
            <TableHeader><TableRow><TableHead>{t("common.name")}</TableHead><TableHead>{t("common.email")}</TableHead><TableHead>{t("settings.role")}</TableHead><TableHead>{t("common.status")}</TableHead>{manage && <TableHead className="text-right">{t("common.actions")}</TableHead>}</TableRow></TableHeader>
          <TableBody>
            {members?.map((m) => (
              <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                <TableCell className="font-medium">{m.user?.full_name}</TableCell>
                <TableCell className="text-slate-500">{m.user?.email}</TableCell>
                <TableCell>{t(`roles.${m.tenant_role}`)}</TableCell>
                <TableCell><StatusBadge status={m.is_active ? "active" : "inactive"} labelKey={m.is_active ? "settings.active" : "settings.inactive"} /></TableCell>
                {manage && <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing({
                      id: m.id,
                      full_name: m.user?.full_name || "",
                      email: m.user?.email || "",
                      tenant_role: m.tenant_role,
                      is_self: m.user_id === user?.id,
                      current_password: "",
                      new_password: "",
                      confirm_password: "",
                    })} data-testid={`edit-member-${m.id}`}><Pencil className="h-4 w-4" /><span className="sr-only">{t("common.edit")}</span></Button>
                    {m.is_active ? m.user_id !== user?.id && <Button variant="ghost" size="sm" onClick={() => setRemoving(m)} data-testid={`remove-member-${m.id}`}><Trash2 className="h-4 w-4 text-red-500" /><span className="sr-only">{t("settings.remove_member")}</span></Button>
                      : <Button variant="ghost" size="sm" onClick={() => reactivateMember(m)} data-testid={`reactivate-member-${m.id}`}><RotateCcw className="h-4 w-4 text-emerald-600" /><span className="sr-only">{t("settings.reactivate")}</span></Button>}
                  </div>
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {invites && invites.length > 0 && (
        <Card>
          <div className="p-4"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.invitations")}</h3></div>
          <Table data-testid="invitations-table">
            <TableHeader><TableRow><TableHead>{t("common.email")}</TableHead><TableHead>{t("settings.role")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead>{t("settings.email_delivery")}</TableHead><TableHead>{t("common.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {invites.map((inv) => (
                <TableRow key={inv.id} data-testid={`invite-row-${inv.id}`}>
                  <TableCell>{inv.email}</TableCell>
                  <TableCell>{t(`roles.${inv.tenant_role}`)}</TableCell>
                  <TableCell><StatusBadge status={inv.status === "pending" ? "sent" : inv.status === "accepted" ? "accepted" : "inactive"} labelKey={`settings.${inv.status}`} /></TableCell>
                  <TableCell>{emailBadge(inv.email_status)}</TableCell>
                  <TableCell>
                    {inv.status === "pending" && manage && (
                      <div className="flex flex-wrap gap-1">
                        <Button variant="ghost" size="sm" onClick={() => resendInvite(inv)} disabled={resendingId === inv.id} data-testid={`resend-invite-${inv.id}`}>
                          {resendingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                          {t("settings.resend_invite")}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => copyInvite(inv)} title={t("settings.copy_invite_link")} data-testid={`copy-invite-${inv.id}`}><Copy className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => revoke(inv)} data-testid={`revoke-invite-${inv.id}`}><Ban className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="member-edit-dialog">
          <DialogHeader><DialogTitle>{t("settings.edit_member")}</DialogTitle><DialogDescription>{t("settings.edit_member_hint")}</DialogDescription></DialogHeader>
          {editing && <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("common.name")}</Label><Input value={editing.full_name} onChange={(e) => setEditing((x) => ({ ...x, full_name: e.target.value }))} data-testid="member-name-input" /></div>
            <div className="space-y-1.5"><Label>{t("common.email")}</Label><Input type="email" value={editing.email} onChange={(e) => setEditing((x) => ({ ...x, email: e.target.value }))} data-testid="member-email-input" /></div>
            <div className="space-y-1.5"><Label>{t("settings.role")}</Label><Select value={editing.tenant_role} onValueChange={(v) => setEditing((x) => ({ ...x, tenant_role: v }))}><SelectTrigger data-testid="member-role-input"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}</SelectContent></Select></div>
            <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
              <h4 className="font-medium">{editing.is_self ? t("settings.change_password") : t("settings.set_temporary_password")}</h4>
              <p className="mt-1 text-xs text-slate-500">{editing.is_self ? t("settings.change_password_hint") : t("settings.temporary_password_hint")}</p>
            </div>
            {editing.is_self && <div className="space-y-1.5"><Label>{t("settings.current_password")}</Label><Input type="password" value={editing.current_password} onChange={(e) => setEditing((x) => ({ ...x, current_password: e.target.value }))} autoComplete="current-password" data-testid="member-current-password-input" /></div>}
            <div className="space-y-1.5"><Label>{t("settings.new_password")}</Label><Input type="password" value={editing.new_password} onChange={(e) => setEditing((x) => ({ ...x, new_password: e.target.value }))} autoComplete="new-password" data-testid="member-new-password-input" /><p className="text-xs text-slate-500">{t("settings.password_optional_minimum")}</p></div>
            <div className="space-y-1.5"><Label>{t("settings.confirm_password")}</Label><Input type="password" value={editing.confirm_password} onChange={(e) => setEditing((x) => ({ ...x, confirm_password: e.target.value }))} autoComplete="new-password" data-testid="member-confirm-password-input" /></div>
            {editing.new_password && editing.confirm_password && editing.new_password !== editing.confirm_password && <p className="text-sm text-red-600">{t("errors.passwords_do_not_match")}</p>}
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button><Button onClick={saveMember} disabled={
            busy || !editing?.full_name.trim() || !editing?.email.trim()
            || (!!editing?.new_password && editing.new_password.length < 10)
            || (!!editing?.new_password && editing.new_password !== editing.confirm_password)
            || (editing?.is_self && !!editing?.new_password && !editing.current_password)
          }>{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent data-testid="member-remove-dialog">
          <DialogHeader><DialogTitle>{t("settings.remove_member")}</DialogTitle><DialogDescription>{t("settings.remove_member_hint", { name: removing?.user?.full_name || "" })}</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setRemoving(null)}>{t("common.cancel")}</Button><Button variant="destructive" onClick={removeMember} disabled={busy}>{t("settings.remove_access")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreferencesTab() {
  const { t, locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  const { updatePreferences } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPassword, setMfaPassword] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/mfa/status").then(({ data }) => setMfaEnabled(!!data.enabled)).catch(() => {});
  }, []);
  const beginMfa = async () => {
    setMfaBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setMfaSetup(data);
      setMfaCode("");
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setMfaBusy(false); }
  };
  const enableMfa = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code: mfaCode });
      setMfaEnabled(true);
      setMfaSetup(null);
      setMfaCode("");
      toast.success(t("settings.mfa_enabled_done"));
    } catch (e) { toast.error(t("settings.mfa_invalid_code")); } finally { setMfaBusy(false); }
  };
  const disableMfa = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: mfaCode, current_password: mfaPassword });
      setMfaEnabled(false);
      setMfaCode("");
      setMfaPassword("");
      toast.success(t("settings.mfa_disabled_done"));
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setMfaBusy(false); }
  };
  const setThemePref = (v) => { setTheme(v); updatePreferences({ theme: v }).catch(() => {}); };
  const setLocalePref = (v) => { setLocale(v); updatePreferences({ locale: v }).catch(() => {}); };
  const THEMES = [{ v: "light", i: Sun, l: "theme_light" }, { v: "dark", i: Moon, l: "theme_dark" }, { v: "system", i: Monitor, l: "theme_system" }];
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.theme")}</h3>
        <div className="flex flex-wrap gap-3" data-testid="theme-options">
          {THEMES.map(({ v, i: Icon, l }) => (
            <button key={v} onClick={() => setThemePref(v)} data-testid={`pref-theme-${v}`}
              className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm transition-colors ${theme === v ? "border-primary bg-primary/10 text-primary" : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"}`}>
              <Icon className="h-4 w-4" />{t(`settings.${l}`)}
            </button>
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.language")}</h3>
        <div className="flex gap-3" data-testid="language-options">
          {[{ v: "de", l: "german" }, { v: "en", l: "english" }].map(({ v, l }) => (
            <button key={v} onClick={() => setLocalePref(v)} data-testid={`pref-lang-${v}`}
              className={`rounded-md border px-4 py-2 text-sm transition-colors ${locale === v ? "border-primary bg-primary/10 text-primary" : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"}`}>
              {t(`settings.${l}`)}
            </button>
          ))}
        </div>
      </Card>
      <Card className="p-6" data-testid="mfa-settings">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div>
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("settings.mfa_title")}</h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">{t("settings.mfa_hint")}</p>
            <p className={`mt-2 text-sm font-medium ${mfaEnabled ? "text-emerald-600" : "text-slate-500"}`}>
              {mfaEnabled ? t("settings.mfa_active") : t("settings.mfa_inactive")}
            </p>
          </div>
          {!mfaEnabled && !mfaSetup && <Button onClick={beginMfa} disabled={mfaBusy}>{t("settings.mfa_start")}</Button>}
        </div>
        {mfaSetup && (
          <div className="mt-5 space-y-4 rounded-lg border p-4">
            <p className="text-sm">{t("settings.mfa_setup_instructions")}</p>
            <div>
              <Label>{t("settings.mfa_setup_key")}</Label>
              <div className="mt-1 break-all rounded-md bg-slate-100 p-3 font-mono text-sm tracking-wider dark:bg-slate-800" data-testid="mfa-secret">{mfaSetup.secret}</div>
            </div>
            <div className="max-w-xs space-y-1.5">
              <Label>{t("auth.mfa_code")}</Label>
              <Input inputMode="numeric" autoComplete="one-time-code" value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={enableMfa} disabled={mfaBusy || mfaCode.length !== 6}>{t("settings.mfa_activate")}</Button>
              <Button variant="outline" onClick={() => { setMfaSetup(null); setMfaCode(""); }}>{t("common.cancel")}</Button>
            </div>
          </div>
        )}
        {mfaEnabled && (
          <div className="mt-5 grid max-w-xl gap-3 rounded-lg border p-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>{t("settings.current_password")}</Label>
              <Input type="password" autoComplete="current-password" value={mfaPassword} onChange={(e) => setMfaPassword(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>{t("auth.mfa_code")}</Label>
              <Input inputMode="numeric" autoComplete="one-time-code" value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} /></div>
            <Button variant="destructive" onClick={disableMfa}
              disabled={mfaBusy || !mfaPassword || mfaCode.length !== 6}>{t("settings.mfa_disable")}</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Settings() {
  const { t } = useI18n();
  const { can, modules } = useAuth();
  const showRegisters = can("pos:configure");
  const showWorkshop = can("settings:write") && modules?.includes("automotive_workshop");
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const requestedTabAllowed = ["setup", "company", "team", "preferences"].includes(requestedTab)
    || (showRegisters && requestedTab === "registers")
    || (showWorkshop && requestedTab === "workshop");
  const [activeTab, setActiveTab] = useState(requestedTabAllowed ? requestedTab : "setup");
  useEffect(() => {
    if (requestedTab && requestedTabAllowed && activeTab !== requestedTab) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, requestedTabAllowed, activeTab]);
  const changeTab = (value) => {
    setActiveTab(value);
    setSearchParams(value === "setup" ? {} : { tab: value }, { replace: true });
  };
  return (
    <div>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />
      <Tabs value={activeTab} onValueChange={changeTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="setup" data-testid="tab-setup">{t("settings.setup")}</TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">{t("settings.company")}</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">{t("settings.team")}</TabsTrigger>
          {showWorkshop && <TabsTrigger value="workshop" data-testid="tab-workshop">{t("workshop.tab")}</TabsTrigger>}
          {showRegisters && <TabsTrigger value="registers" data-testid="tab-registers">{t("posadmin.tab")}</TabsTrigger>}
          <TabsTrigger value="preferences" data-testid="tab-preferences">{t("settings.preferences")}</TabsTrigger>
        </TabsList>
        <TabsContent value="setup"><SetupTab onOpenTab={changeTab} /></TabsContent>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        <TabsContent value="team"><TeamTab /></TabsContent>
        {showWorkshop && <TabsContent value="workshop"><WorkshopTab /></TabsContent>}
        {showRegisters && <TabsContent value="registers"><RegistersTab /></TabsContent>}
        <TabsContent value="preferences"><PreferencesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
